// src/Pages/Assets/DepositPage.jsx
import React, { useEffect, useMemo, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchMyDepositAddresses,
  createDepositIntent,
  clearActiveIntent,
} from "../../store/Slices/depositSlice";
import { fetchSpotBalances } from "../../store/Slices/assetSlice";
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  TextField,
  MenuItem,
  Button,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  LinearProgress,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import RefreshIcon from "@mui/icons-material/Refresh";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import HistoryIcon from "@mui/icons-material/History";
import { QRCodeCanvas } from "qrcode.react";
import { useNavigate } from "react-router-dom";
import AppTopBar from "../../Components/AppTopBar";

const useBrand = () => {
  const theme = useTheme();
  return (
    theme.palette?.brand?.main || theme.palette?.primary?.main || "#f0b90b"
  );
};

const Copyable = ({ text, children }) => {
  const [copied, setCopied] = useState(false);
  return (
    <Tooltip title={copied ? "Copied!" : "Copy"} placement="top">
      <IconButton
        size="small"
        onClick={() => {
          navigator.clipboard.writeText(String(text));
          setCopied(true);
          setTimeout(() => setCopied(false), 1000);
        }}
      >
        {children || <ContentCopyIcon fontSize="small" />}
      </IconButton>
    </Tooltip>
  );
};

function Row({ k, v, copy = false, shrink = false }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Typography
        variant="body2"
        sx={{ minWidth: 150, color: "text.secondary" }}
      >
        {k}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontWeight: 700,
          ...(shrink
            ? {
                maxWidth: "100%",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }
            : {}),
        }}
      >
        {v}
      </Typography>
      {copy && <Copyable text={v} />}
    </Box>
  );
}

export default function DepositPage() {
  const dispatch = useDispatch();
  const theme = useTheme();
  const brand = useBrand();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();

  const {
    addresses,
    addressesLoading,
    activeIntent,
    creating,
    error,
  } = useSelector((s) => s.deposit);
  const { spotBalances } = useSelector((s) => s.assets);

  const [asset, setAsset] = useState("USDT");
  const [network, setNetwork] = useState("");
  const [amount, setAmount] = useState("");
  const [countdown, setCountdown] = useState(null);
  const timerRef = useRef(null);

  useEffect(() => {
    dispatch(fetchMyDepositAddresses());
    dispatch(fetchSpotBalances());
  }, [dispatch]);

  const networksForAsset = useMemo(() => {
    const filtered = addresses.filter(
      (a) => a.asset_symbol?.toLowerCase() === asset.toLowerCase()
    );
    return [...new Set(filtered.map((f) => f.network_name))];
  }, [addresses, asset]);

  useEffect(() => {
    if (!network && networksForAsset.length) setNetwork(networksForAsset[0]);
  }, [networksForAsset, network]);

  const selectedAddress = useMemo(() => {
    return (
      addresses.find(
        (a) =>
          a.asset_symbol?.toLowerCase() === asset.toLowerCase() &&
          a.network_name === network
      )?.address || ""
    );
  }, [addresses, asset, network]);

  const handleCreateIntent = async () => {
    if (!asset || !network || !amount || !selectedAddress) return;
    await dispatch(
      createDepositIntent({
        asset_symbol: asset,
        network_name: network,
        amount: Number(amount),
      })
    )
      .unwrap()
      .catch(() => {});
  };

  const resetForm = () => {
    setAmount("");
    dispatch(clearActiveIntent());
    setCountdown(null);
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  useEffect(() => {
    if (!activeIntent?.expires_at) return;
    if (timerRef.current) clearInterval(timerRef.current);
    const update = () => {
      const diffMs = new Date(activeIntent.expires_at).getTime() - Date.now();
      if (diffMs <= 0) {
        setCountdown("Expired");
        clearInterval(timerRef.current);
        timerRef.current = null;
        return;
      }
      const mins = Math.floor(diffMs / 60000);
      const secs = Math.floor((diffMs % 60000) / 1000);
      setCountdown(`${mins}m ${String(secs).padStart(2, "0")}s`);
    };
    update();
    timerRef.current = setInterval(update, 1000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [activeIntent]);

  return (
    <Box sx={{ bgcolor: theme.palette.background.default, minHeight: "100vh" }}>
      <AppTopBar
        title="Deposit"
        rightSlot={
          <Button
            startIcon={<HistoryIcon />}
            onClick={() => navigate("/deposit/records")}
            sx={{ color: theme.palette.text.primary }}
          >
            Intents & History
          </Button>
        }
      />
      <Container maxWidth="lg" sx={{ pt: 10, pb: 4 }}>
        <Grid container spacing={isMobile ? 2 : 3}>
          {/* الفورم + تفاصيل الوجهة */}
          <Grid item xs={12} md={7}>
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: 2,
                border: `1px solid ${theme.palette.text.secondary}22`,
              }}
            >
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 700 }}>
                Choose Asset & Network
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    label="Asset"
                    value={asset}
                    onChange={(e) => {
                      setAsset(e.target.value);
                      setNetwork("");
                      setAmount("");
                      dispatch(clearActiveIntent());
                    }}
                  >
                    {[...new Set(addresses.map((a) => a.asset_symbol))].map(
                      (sym) => (
                        <MenuItem key={sym} value={sym}>
                          {sym}
                        </MenuItem>
                      )
                    )}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    label="Network"
                    value={network}
                    onChange={(e) => {
                      setNetwork(e.target.value);
                      setAmount("");
                      dispatch(clearActiveIntent());
                    }}
                  >
                    {networksForAsset.map((n) => (
                      <MenuItem key={n} value={n}>
                        {n}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    type="number"
                    label={`Amount (${asset})`}
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    inputProps={{ min: 0, step: "0.000001" }}
                  />
                </Grid>

                <Grid
                  item
                  xs={12}
                  sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}
                >
                  <Button
                    variant="contained"
                    onClick={handleCreateIntent}
                    disabled={
                      !asset ||
                      !network ||
                      !amount ||
                      creating ||
                      !selectedAddress
                    }
                    sx={{
                      bgcolor: brand,
                      color: theme.palette.text.primary,
                      borderRadius: 2,
                      px: 3,
                      "&:hover": { bgcolor: brand },
                    }}
                  >
                    {creating ? "Creating..." : "Create Deposit Intent"}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => dispatch(fetchMyDepositAddresses())}
                    startIcon={<RefreshIcon />}
                  >
                    Refresh Addresses
                  </Button>
                  <Button
                    variant="text"
                    onClick={() => navigate("/deposit/records")}
                  >
                    View Intents & History
                  </Button>
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                Deposit Address
              </Typography>
              {addressesLoading ? (
                <LinearProgress />
              ) : selectedAddress ? (
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: `1px dashed ${theme.palette.text.secondary}55`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                  }}
                >
                  <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                    {selectedAddress}
                  </Typography>
                  <Copyable text={selectedAddress} />
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Select asset & network to view the address.
                </Typography>
              )}

              <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Chip
                  icon={<InfoOutlinedIcon />}
                  label="Send exact amount"
                  size="small"
                />
                <Chip label="Use correct network" size="small" />
                <Chip label="One-time intent" size="small" />
              </Box>
            </Paper>

            {/* Intent النشط + QR */}
            {activeIntent && (
              <Paper
                elevation={0}
                sx={{
                  mt: 2,
                  p: 2.5,
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.text.secondary}22`,
                }}
              >
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
                >
                  <QrCode2Icon />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Payment Details
                  </Typography>
                  {!!countdown && (
                    <Chip
                      size="small"
                      sx={{ ml: "auto", bgcolor: `${brand}20`, color: brand }}
                      label={`Expires in: ${countdown}`}
                    />
                  )}
                </Box>
                <Grid container spacing={2}>
                  <Grid
                    item
                    xs={12}
                    md={5}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <QRCodeCanvas
                      value={JSON.stringify({
                        address: activeIntent.address,
                        amount: activeIntent.amount_to_send,
                        memo_tag: activeIntent.memo_tag || undefined,
                        asset,
                        network,
                      })}
                      size={isMobile ? 180 : 200}
                      includeMargin
                    />
                  </Grid>
                  <Grid item xs={12} md={7}>
                    <Box sx={{ display: "grid", gap: 1.2 }}>
                      <Row k="Address" v={activeIntent.address} copy />
                      <Row
                        k="Amount to Send"
                        v={`${activeIntent.amount_to_send} ${asset}`}
                      />
                      {activeIntent.memo_tag && (
                        <Row
                          k="Destination Tag / Memo"
                          v={activeIntent.memo_tag}
                          copy
                        />
                      )}
                      <Row k="Network" v={network} />
                      <Row
                        k="Expires At"
                        v={new Date(activeIntent.expires_at).toLocaleString()}
                      />
                    </Box>
                    <Box sx={{ mt: 2, display: "flex", gap: 1 }}>
                      <Button onClick={resetForm} variant="outlined">
                        New Intent
                      </Button>
                      <Button
                        variant="contained"
                        sx={{
                          bgcolor: brand,
                          color: theme.palette.text.primary,
                          "&:hover": { bgcolor: brand },
                        }}
                        onClick={() =>
                          window.open(`https://www.oklink.com/`, "_blank")
                        }
                      >
                        Open Explorer
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            )}
          </Grid>

          {/* Snapshot سريع للأرصدة */}
          <Grid item xs={12} md={5}>
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: 2,
                border: `1px solid ${theme.palette.text.secondary}22`,
              }}
            >
              <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 700 }}>
                Spot Snapshot
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gap: 1.2,
                  maxHeight: 420,
                  overflow: "auto",
                }}
              >
                {(spotBalances || [])
                  .filter((b) => b?.asset_id?.symbol)
                  .map((b) => (
                    <Paper
                      key={b._id}
                      variant="outlined"
                      sx={{
                        p: 1.2,
                        borderRadius: 2,
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <Typography variant="body2">
                        {b.asset_id.symbol}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {b.balance}
                      </Typography>
                    </Paper>
                  ))}
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {addressesLoading && <LinearProgress sx={{ mt: 2 }} />}
        {error && (
          <Typography sx={{ mt: 2, color: "error.main" }}>
            {String(error)}
          </Typography>
        )}
      </Container>
    </Box>
  );
}
